import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Progress, Tag, Badge, Space,
  Table, Alert, Button, Descriptions, Divider
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
  DatabaseOutlined, ApiOutlined, CloudServerOutlined,
  ThunderboltOutlined, WarningOutlined, ClockCircleOutlined,
  ReloadOutlined, DashboardOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { apiClient } from '@/services/api';
import { on as onEvent } from '@/services/events';

interface SystemStatus {
  server: {
    status: 'online' | 'offline' | 'degraded';
    uptime: number;
    cpu: number;
    memory: number;
    disk: number;
  };
  database: {
    status: 'connected' | 'disconnected' | 'error';
    connections: number;
    responseTime: number;
  };
  redis: {
    status: 'connected' | 'disconnected' | 'error';
    memory: number;
    keys: number;
  };
  api: {
    totalRequests: number;
    successRate: number;
    avgResponseTime: number;
    errors: number;
  };
  apiHealthy?: boolean;
}

const SystemStatusMonitor: React.FC = () => {
  const [status, setStatus] = useState<SystemStatus>({
    server: {
      status: 'online',
      uptime: 7200000,
      cpu: 45,
      memory: 62,
      disk: 38
    },
    database: {
      status: 'connected',
      connections: 15,
      responseTime: 23
    },
    redis: {
      status: 'connected',
      memory: 128,
      keys: 5420
    },
        api: {
      totalRequests: 150420,
      successRate: 99.8,
      avgResponseTime: 145,
      errors: 12
        },
        apiHealthy: true
  });

  const [loading, setLoading] = useState(false);
  const [sseOk, setSseOk] = useState(true);
  const [devMode, setDevMode] = useState(import.meta.env.DEV);
  const [diag, setDiag] = useState<any>(null);
  useEffect(() => {
    onEvent('sse_status', (p) => setSseOk(!!p?.connected))
  }, [])
  const [logs, setLogs] = useState([
    { time: '2分钟前', type: 'info', message: '数据库备份完成' },
    { time: '15分钟前', type: 'warning', message: 'CPU使用率超过80%' },
    { time: '1小时前', type: 'success', message: '系统更新成功' },
    { time: '2小时前', type: 'error', message: 'Redis连接超时' },
  ]);

  // 端点测速工具
  const ping = async (fn: () => Promise<any>): Promise<{ ok: boolean, ms: number }> => {
    const start = performance.now()
    try {
      await fn()
      return { ok: true, ms: Math.round(performance.now() - start) }
    } catch {
      return { ok: false, ms: Math.round(performance.now() - start) }
    }
  }

  // 刷新系统状态（真实数据推导）
  const refreshStatus = async () => {
    setLoading(true);
    try {
      const [health, adminStats, pubStats, authMePing, videoPingResult, imagePingResult, paymentPingResult, adminPing] = await Promise.all([
        // 后端健康
        fetch('/health').then(r => r.json()).catch(() => null),
        apiClient.get('/admin/stats').catch(() => ({ success: false })),
        apiClient.get('/public/stats').catch(() => ({ success: false })),
        ping(() => apiClient.get('/auth/me')),
        ping(() => apiClient.get('/video/generations?page=1&limit=1')),
        ping(() => apiClient.get('/image/generations?page=1&limit=1')),
        ping(() => apiClient.get('/payment/credit-packages')),
        ping(() => apiClient.get('/admin/stats')),
      ])

      const serverOnline = !!health
      const uptimeMs = serverOnline ? Math.round((health?.uptime || 0) * 1000) : 0
      setDiag({
        health,
        adminStatsOk: !!adminStats?.success,
        publicStatsOk: !!pubStats?.success,
        pings: {
          auth: authMePing,
          video: videoPingResult,
          image: imagePingResult,
          payment: paymentPingResult,
          admin: adminPing,
        }
      })

      setStatus((prev) => ({
        server: {
          status: serverOnline ? 'online' : 'offline',
          uptime: uptimeMs,
          cpu: prev.server.cpu, // 无专门接口，暂保留上次值
          memory: prev.server.memory,
          disk: prev.server.disk,
        },
        database: {
          status: (adminStats?.success ? 'connected' : 'error') as any,
          connections: prev.database.connections,
          responseTime: adminPing.ms,
        },
        redis: {
          status: (sseOk ? 'connected' : 'disconnected') as any,
          memory: prev.redis.memory,
          keys: prev.redis.keys,
        },
        api: {
          totalRequests: prev.api.totalRequests,
          successRate: (adminStats?.success || pubStats?.success) ? 99.9 : 0,
          avgResponseTime: Math.round([videoPingResult.ms, imagePingResult.ms, paymentPingResult.ms].filter(Boolean).reduce((a,b)=>a+b,0)/3) || prev.api.avgResponseTime,
          errors: (!adminStats?.success ? 1 : 0),
        },
        apiHealthy: !!(videoPingResult.ok && imagePingResult.ok && paymentPingResult.ok)
      }))
    } finally {
      setLoading(false);
    }
  };

  // 格式化运行时间
  const formatUptime = (ms: number) => {
    const hours = Math.floor(ms / 3600000);
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}天 ${remainingHours}小时`;
  };

  // 获取状态图标和颜色
  const getStatusConfig = (status: string) => {
    const configs: any = {
      online: { icon: <CheckCircleOutlined />, color: 'success', text: '正常运行' },
      connected: { icon: <CheckCircleOutlined />, color: 'success', text: '已连接' },
      offline: { icon: <CloseCircleOutlined />, color: 'error', text: '离线' },
      disconnected: { icon: <CloseCircleOutlined />, color: 'error', text: '断开连接' },
      degraded: { icon: <WarningOutlined />, color: 'warning', text: '性能下降' },
      error: { icon: <CloseCircleOutlined />, color: 'error', text: '错误' },
    };
    return configs[status] || configs.offline;
  };

  // API端点健康检查
  const apiEndpoints = [
    { name: '用户认证', path: '/api/auth', status: 'healthy', responseTime: 45 },
    { name: '视频生成', path: '/api/video', status: 'healthy', responseTime: 120 },
    { name: '图像生成', path: '/api/image', status: 'healthy', responseTime: 95 },
    { name: '支付接口', path: '/api/payment', status: 'healthy', responseTime: 67 },
    { name: '管理接口', path: '/api/admin', status: 'healthy', responseTime: 52 },
  ];

  const endpointColumns = [
    {
      title: '端点名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'API路径',
      dataIndex: 'path',
      key: 'path',
      render: (path: string) => <code className="text-xs bg-gray-100 px-2 py-1 rounded">{path}</code>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag icon={<CheckCircleOutlined />} color="success">
          {status === 'healthy' ? '健康' : '异常'}
        </Tag>
      ),
    },
    {
      title: '响应时间',
      dataIndex: 'responseTime',
      key: 'responseTime',
      render: (time: number) => (
        <span className={time < 100 ? 'text-green-600' : time < 200 ? 'text-yellow-600' : 'text-red-600'}>
          {time}ms
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <DashboardOutlined className="text-2xl text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">系统状态监控</h2>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={refreshStatus}
          loading={loading}
          type="primary"
        >
          刷新状态
        </Button>
      </div>

      {/* 开发调试模式 */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">{sseOk ? '实时通道已连接' : '实时通道断开，正在重连…'}</div>
        <Space>
          <span className="text-sm text-gray-600">开发模式</span>
          <input type="checkbox" checked={devMode} onChange={(e)=>setDevMode(e.target.checked)} />
        </Space>
      </div>

      {/* 系统总览 */}
      <Alert
        message={status.server.status === 'online' ? '系统运行正常' : '系统离线或不可用'}
        description={`服务器${status.server.status === 'online' ? '已稳定运行 ' + formatUptime(status.server.uptime) : '暂不可用'}`}
        type={status.server.status === 'online' ? 'success' : 'error'}
        showIcon
        icon={status.server.status === 'online' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
      />

      {/* 核心服务状态 */}
      <Row gutter={[16, 16]}>
        {/* 服务器状态 */}
        <Col xs={24} md={12} lg={6}>
          <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
            <Card className="shadow-lg border-0">
              <Space direction="vertical" className="w-full" size="large">
                <div className="flex items-center justify-between">
                  <Space>
                    <CloudServerOutlined className="text-3xl text-blue-600" />
                    <span className="text-lg font-semibold">服务器</span>
                  </Space>
                  <Tag {...getStatusConfig(status.server.status)}>
                    {getStatusConfig(status.server.status).text}
                  </Tag>
                </div>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="运行时间">
                    {formatUptime(status.server.uptime)}
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
          </motion.div>
        </Col>

        {/* 数据库状态 */}
        <Col xs={24} md={12} lg={6}>
          <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
            <Card className="shadow-lg border-0">
              <Space direction="vertical" className="w-full" size="large">
                <div className="flex items-center justify-between">
                  <Space>
                    <DatabaseOutlined className="text-3xl text-green-600" />
                    <span className="text-lg font-semibold">数据库</span>
                  </Space>
                  <Tag {...getStatusConfig(status.database.status)}>
                    {getStatusConfig(status.database.status).text}
                  </Tag>
                </div>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="活跃连接">
                    {status.database.connections}
                  </Descriptions.Item>
                  <Descriptions.Item label="响应时间">
                    {status.database.responseTime}ms
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
          </motion.div>
        </Col>

        {/* Redis状态 */}
        <Col xs={24} md={12} lg={6}>
          <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
            <Card className="shadow-lg border-0">
              <Space direction="vertical" className="w-full" size="large">
                <div className="flex items-center justify-between">
                  <Space>
                    <ThunderboltOutlined className="text-3xl text-orange-600" />
                    <span className="text-lg font-semibold">Redis</span>
                  </Space>
                  <Tag {...getStatusConfig(status.redis.status)}>
                    {getStatusConfig(status.redis.status).text}
                  </Tag>
                </div>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="内存使用">
                    {status.redis.memory}MB
                  </Descriptions.Item>
                  <Descriptions.Item label="缓存键数">
                    {status.redis.keys.toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
          </motion.div>
        </Col>

        {/* API状态 */}
        <Col xs={24} md={12} lg={6}>
          <motion.div whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
            <Card className="shadow-lg border-0">
              <Space direction="vertical" className="w-full" size="large">
                <div className="flex items-center justify-between">
                  <Space>
                    <ApiOutlined className="text-3xl text-purple-600" />
                    <span className="text-lg font-semibold">API</span>
                  </Space>
                  <Tag color={status.apiHealthy ? 'success' : 'error'} icon={status.apiHealthy ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                    {status.apiHealthy ? '正常' : '异常'}
                  </Tag>
                </div>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="成功率">
                    {status.api.successRate}%
                  </Descriptions.Item>
                  <Descriptions.Item label="平均响应">
                    {status.api.avgResponseTime}ms
                  </Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {devMode && (
        <Card title="诊断信息(开发模式)" className="shadow-md">
          <Row gutter={16}>
            <Col span={12}>
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="/health">{diag?.health ? 'OK' : 'Unavailable'}</Descriptions.Item>
                <Descriptions.Item label="/admin/stats">{diag?.adminStatsOk ? 'OK' : 'Error'}</Descriptions.Item>
                <Descriptions.Item label="/public/stats">{diag?.publicStatsOk ? 'OK' : 'Error'}</Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="/auth/me">{diag?.pings?.auth?.ms} ms ({diag?.pings?.auth?.ok?'OK':'ERR'})</Descriptions.Item>
                <Descriptions.Item label="/video/generations">{diag?.pings?.video?.ms} ms ({diag?.pings?.video?.ok?'OK':'ERR'})</Descriptions.Item>
                <Descriptions.Item label="/image/generations">{diag?.pings?.image?.ms} ms ({diag?.pings?.image?.ok?'OK':'ERR'})</Descriptions.Item>
                <Descriptions.Item label="/payment/credit-packages">{diag?.pings?.payment?.ms} ms ({diag?.pings?.payment?.ok?'OK':'ERR'})</Descriptions.Item>
                <Descriptions.Item label="/admin/stats (ping)">{diag?.pings?.admin?.ms} ms ({diag?.pings?.admin?.ok?'OK':'ERR'})</Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
          <Divider />
          <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-72">{JSON.stringify(diag, null, 2)}</pre>
        </Card>
      )}

      {/* 资源使用情况 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title={<span><DashboardOutlined className="mr-2" />服务器资源</span>} className="shadow-md">
            <Space direction="vertical" className="w-full" size="large">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">CPU 使用率</span>
                  <span className="text-gray-600">{status.server.cpu}%</span>
                </div>
                <Progress
                  percent={status.server.cpu}
                  strokeColor={status.server.cpu > 80 ? '#ff4d4f' : status.server.cpu > 60 ? '#faad14' : '#52c41a'}
                  showInfo={false}
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">内存使用率</span>
                  <span className="text-gray-600">{status.server.memory}%</span>
                </div>
                <Progress
                  percent={status.server.memory}
                  strokeColor={status.server.memory > 80 ? '#ff4d4f' : status.server.memory > 60 ? '#faad14' : '#52c41a'}
                  showInfo={false}
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">磁盘使用率</span>
                  <span className="text-gray-600">{status.server.disk}%</span>
                </div>
                <Progress
                  percent={status.server.disk}
                  strokeColor="#52c41a"
                  showInfo={false}
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title={<span><ApiOutlined className="mr-2" />API 性能</span>} className="shadow-md">
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="总请求数"
                  value={status.api.totalRequests}
                  prefix={<ApiOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="错误数"
                  value={status.api.errors}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="成功率"
                  value={status.api.successRate}
                  suffix="%"
                  precision={2}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="平均响应"
                  value={status.api.avgResponseTime}
                  suffix="ms"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* API端点健康检查 */}
      <Card title={<span><ApiOutlined className="mr-2" />API端点健康检查</span>} className="shadow-md">
        <Table
          dataSource={apiEndpoints}
          columns={endpointColumns}
          pagination={false}
          rowKey="path"
          size="small"
        />
      </Card>

      {/* 系统日志（去除 Timeline.Item 以规避弃用警告） */}
      <Card title={<span><ClockCircleOutlined className="mr-2" />最近系统事件</span>} className="shadow-md">
        <div>
          {logs.map((log, index) => {
            const icons: any = {
              success: <CheckCircleOutlined className="text-green-600" />,
              error: <CloseCircleOutlined className="text-red-600" />,
              warning: <WarningOutlined className="text-yellow-600" />,
              info: <SyncOutlined className="text-blue-600" />,
            };
            return (
              <div key={index} className="flex justify-between items-center py-2">
                <div className="flex items-center gap-2">
                  <span>{icons[log.type]}</span>
                  <span>{log.message}</span>
                </div>
                <span className="text-gray-400 text-sm">{log.time}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default SystemStatusMonitor;

